import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { API_URL } from '../utils/cartApi';

const getSavedWishlist = () => {
  try {
    const savedWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    return Array.isArray(savedWishlist) ? savedWishlist : [];
  } catch (error) {
    return [];
  }
};

const Wishlist = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadWishlist = async () => {
      try {
        const response = await fetch(`${API_URL}/api/products`);
        const responseData = await response.json();
        const allProducts = Array.isArray(responseData) ? responseData : responseData.products || [];
        const savedIds = getSavedWishlist().map(String);

        setProducts(allProducts.filter((product) => savedIds.includes(String(product._id || product.id))));
      } catch (error) {
        console.error('Error loading wishlist:', error);
      } finally {
        setLoading(false);
      }
    };

    loadWishlist();
  }, []);

  const removeFromWishlist = (productId) => {
    const nextWishlist = getSavedWishlist().filter((id) => String(id) !== String(productId));
    localStorage.setItem('wishlist', JSON.stringify(nextWishlist));
    window.dispatchEvent(new Event('wishlistUpdated'));
    setProducts((currentProducts) => currentProducts.filter((product) => String(product._id || product.id) !== String(productId)));
  };

  return (
    <main
      className="min-h-screen bg-cover bg-repeat px-6 py-12 text-[#1A1A1A]"
      style={{ backgroundImage: "url('/bg-parchment.jpg')" }}
    >
      <div className="mx-auto max-w-7xl">
        <div className="mb-10 border-b border-[#1A1A1A]/10 pb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#A39171]">Saved for later</p>
          <h1 className="mt-3 font-serif text-4xl font-normal">Your Wishlist</h1>
        </div>

        {loading && <p className="py-12 text-center text-sm text-[#1A1A1A]/60">Loading your saved sarees...</p>}

        {!loading && products.length === 0 && (
          <div className="py-16 text-center">
            <Heart className="mx-auto mb-4 text-[#A39171]" size={28} />
            <p className="font-serif text-2xl">Your wishlist is waiting.</p>
            <Link to="/explore" className="mt-5 inline-block text-xs font-semibold uppercase tracking-[0.16em] underline underline-offset-4">Explore sarees</Link>
          </div>
        )}

        {!loading && products.length > 0 && (
          <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
            {products.map((product) => {
              const productId = product._id || product.id;
              const image = product.image || product.imageUrl || product.images?.[0] || '/placeholder.png';

              return (
                <article key={productId}>
                  <div className="relative aspect-[3/4] overflow-hidden border border-[#1A1A1A]/5 bg-white/40 shadow-sm">
                    <Link to={`/product/${productId}`}>
                      <img src={image} alt={product.name || product.title} className="h-full w-full object-cover" />
                    </Link>
                    <button
                      type="button"
                      onClick={() => removeFromWishlist(productId)}
                      aria-label="Remove from Wishlist"
                      className="absolute right-3 top-3 rounded-full bg-white/80 p-2 text-rose-600"
                    >
                      <Heart size={16} fill="currentColor" />
                    </button>
                  </div>
                  <h2 className="mt-4 truncate font-serif text-lg">{product.name || product.title}</h2>
                  <p className="mt-1 text-sm font-semibold">₹{product.price}</p>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

export default Wishlist;
